import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import { Marca } from '../componentes/Marca'
import './agendar.css'

/**
 * A página pública de agendamento.
 *
 * Chega por link ou QR, sem sessão. É a única tela do Clinuma que
 * qualquer pessoa da internet alcança, e por isso ela mostra o mínimo:
 * o nome da clínica, quem atende, o que é feito e os horários livres.
 * Nada de paciente, nada de ocupação, nada que sirva para sondar a base.
 *
 * Quatro passos, um por vez. Formulário longo numa tela só é como
 * metade das pessoas desiste antes de terminar — e aqui a pessoa não
 * tem conta nem motivo para insistir.
 */

type Opcao = {
  id: string; nome: string; cor: string | null
  duracao_min: number | null; valor_centavos: string | null
}

type Vitrine = {
  clinica: string; titulo: string | null; aviso: string | null
  antecedencia_min_horas: number; janela_max_dias: number
  exige_cpf: boolean; exige_nascimento: boolean; aceita_novo: boolean
  profissionais: Opcao[]; procedimentos: Opcao[]
}

const brl = (c: string | null) =>
  c === null ? null : (Number(c) / 100).toLocaleString('pt-BR',
    { style: 'currency', currency: 'BRL' })

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

const diaLongo = (d: Date) => {
  const t = d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })
  return t.charAt(0).toUpperCase() + t.slice(1)
}

const iso = (d: Date) => d.toISOString().slice(0, 10)
const digitos = (s: string) => s.replace(/\D/g, '')

const mascaraTel = (s: string) => {
  const d = digitos(s).slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

export function Agendar({ slug }: { slug: string }) {
  const [v, setV] = useState<Vitrine | null>(null)
  const [erroCarga, setErroCarga] = useState(false)
  const [passo, setPasso] = useState(1)

  const [proc, setProc] = useState<Opcao | null>(null)
  const [prof, setProf] = useState<Opcao | null>(null)
  const [dia, setDia] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d })
  const [horarios, setHorarios] = useState<Array<{ inicio: string }>>([])
  const [buscando, setBuscando] = useState(false)
  const [escolhido, setEscolhido] = useState<string | null>(null)

  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [cpf, setCpf] = useState('')
  const [nascimento, setNascimento] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [pronto, setPronto] = useState(false)

  useEffect(() => {
    void (async () => {
      try { setV(await api<Vitrine>(`/v1/publico/${slug}`)) }
      catch { setErroCarga(true) }
    })()
  }, [slug])

  const buscarHorarios = useCallback(async () => {
    if (!prof || !proc) return
    setBuscando(true); setEscolhido(null)
    try {
      const r = await api<{ horarios: Array<{ inicio: string }> }>(
        `/v1/publico/${slug}/horarios?profissional=${prof.id}&procedimento=${proc.id}&dia=${iso(dia)}`)
      setHorarios(r.horarios)
    } catch { setHorarios([]) }
    finally { setBuscando(false) }
  }, [slug, prof, proc, dia])

  useEffect(() => { if (passo === 3) void buscarHorarios() }, [passo, buscarHorarios])

  async function confirmar(e: React.FormEvent) {
    e.preventDefault()
    if (!prof || !proc || !escolhido) return
    setErro(null); setEnviando(true)
    try {
      await api(`/v1/publico/${slug}/agendar`, {
        metodo: 'POST',
        corpo: {
          profissional: prof.id, procedimento: proc.id, inicio: escolhido,
          nome: nome.trim(), telefone: digitos(telefone),
          ...(digitos(cpf) ? { cpf: digitos(cpf) } : {}),
          ...(nascimento ? { nascimento } : {}),
        },
      })
      setPronto(true)
    } catch (err) {
      // 409 é o caso que mais acontece: alguém marcou o mesmo horário
      // enquanto esta pessoa preenchia. Voltar para a lista é mais útil
      // do que só avisar.
      if (err instanceof ErroApi && err.status === 409) {
        setErro('Esse horário acabou de ser ocupado. Escolha outro.')
        setPasso(3); void buscarHorarios()
      } else {
        setErro(err instanceof ErroApi ? err.message : 'Não foi possível marcar.')
      }
      setEnviando(false)
    }
  }

  if (erroCarga) {
    return (
      <main className="ag">
        <div className="ag__caixa">
          <Marca tamanho={28} />
          <h1>Agendamento indisponível</h1>
          <p className="ajuda">
            Esta clínica não está aceitando marcação pela internet no momento.
            Entre em contato por telefone.
          </p>
        </div>
      </main>
    )
  }

  if (!v) {
    return <main className="ag"><div className="ag__caixa"><Marca tamanho={28} />
      <p className="ajuda">Carregando…</p></div></main>
  }

  if (pronto) {
    return (
      <main className="ag">
        <div className="ag__caixa ag__ok">
          <Marca tamanho={28} />
          <h1>Horário reservado</h1>
          <p className="ag__resumo">
            <strong>{proc?.nome}</strong> com <strong>{prof?.nome}</strong><br />
            {diaLongo(dia)} às <strong>{escolhido && hora(escolhido)}</strong>
          </p>
          <p className="ajuda">
            A clínica vai confirmar com você. Se precisar desmarcar, ligue para
            {' '}{v.clinica} — quanto antes avisar, melhor para quem está esperando.
          </p>
        </div>
      </main>
    )
  }

  const maxDia = new Date(); maxDia.setDate(maxDia.getDate() + v.janela_max_dias)
  const minDia = new Date(); minDia.setHours(minDia.getHours() + v.antecedencia_min_horas)

  return (
    <main className="ag">
      <div className="ag__caixa">
        <header className="ag__topo">
          <Marca tamanho={24} />
          <div>
            <h1>{v.titulo || v.clinica}</h1>
            {v.titulo && <p className="ajuda">{v.clinica}</p>}
          </div>
        </header>

        {v.aviso && <p className="ag__aviso">{v.aviso}</p>}

        <ol className="ag__passos" aria-label={`Passo ${passo} de 4`}>
          {['Serviço', 'Profissional', 'Horário', 'Seus dados'].map((t, i) => (
            <li key={t} className={i + 1 === passo ? 'is-atual' : i + 1 < passo ? 'is-feito' : ''}>
              <span>{t}</span>
            </li>
          ))}
        </ol>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {/* ── 1 · o que ─────────────────────────────────────────── */}
        {passo === 1 && (
          <section className="ag__lista" aria-label="Escolha o serviço">
            {v.procedimentos.length === 0 ? (
              <p className="ajuda">Nenhum serviço disponível para marcação online.</p>
            ) : v.procedimentos.map((p) => (
              <button key={p.id} className="ag__opcao"
                      onClick={() => { setProc(p); setPasso(2) }}>
                <span className="ag__opcao-nome">{p.nome}</span>
                <span className="ag__opcao-meta">
                  {p.duracao_min && `${p.duracao_min} min`}
                  {p.valor_centavos && Number(p.valor_centavos) > 0 &&
                    ` · ${brl(p.valor_centavos)}`}
                </span>
              </button>
            ))}
          </section>
        )}

        {/* ── 2 · com quem ──────────────────────────────────────── */}
        {passo === 2 && (
          <section className="ag__lista" aria-label="Escolha o profissional">
            {v.profissionais.map((p) => (
              <button key={p.id} className="ag__opcao"
                      onClick={() => { setProf(p); setPasso(3) }}>
                {p.cor && <i className="ag__cor" style={{ background: p.cor }} />}
                <span className="ag__opcao-nome">{p.nome}</span>
              </button>
            ))}
            <button className="ag__voltar" onClick={() => setPasso(1)}>← Trocar o serviço</button>
          </section>
        )}

        {/* ── 3 · quando ────────────────────────────────────────── */}
        {passo === 3 && (
          <section aria-label="Escolha o horário">
            <div className="campo">
              <label htmlFor="ag-dia">Dia</label>
              <input id="ag-dia" className="entrada" type="date"
                     value={iso(dia)}
                     min={iso(minDia)} max={iso(maxDia)}
                     onChange={(e) => e.target.value && setDia(new Date(e.target.value + 'T12:00:00'))} />
            </div>

            {buscando ? (
              <p className="ajuda">Procurando horários…</p>
            ) : horarios.length === 0 ? (
              /* Não dizer POR QUE não há vaga: agenda cheia, dia de folga
                 e profissional fora do ar dão a mesma resposta. */
              <p className="ag__vazio">
                Sem horário livre neste dia. Tente outro.
              </p>
            ) : (
              <div className="ag__horarios">
                {horarios.map((h) => (
                  <button key={h.inicio}
                          className={`ag__hora ${escolhido === h.inicio ? 'is-escolhido' : ''}`}
                          onClick={() => { setEscolhido(h.inicio); setPasso(4) }}>
                    {hora(h.inicio)}
                  </button>
                ))}
              </div>
            )}
            <button className="ag__voltar" onClick={() => setPasso(2)}>← Trocar o profissional</button>
          </section>
        )}

        {/* ── 4 · quem é ────────────────────────────────────────── */}
        {passo === 4 && (
          <form onSubmit={confirmar} className="ag__form">
            <p className="ag__resumo">
              <strong>{proc?.nome}</strong> com <strong>{prof?.nome}</strong><br />
              {diaLongo(dia)} às <strong>{escolhido && hora(escolhido)}</strong>
            </p>

            <div className="campo">
              <label htmlFor="ag-nome">Seu nome completo</label>
              <input id="ag-nome" className="entrada" required autoFocus
                     autoComplete="name" minLength={3} maxLength={120}
                     value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>

            <div className="campo">
              <label htmlFor="ag-tel">Celular com DDD</label>
              <input id="ag-tel" className="entrada mono" required
                     inputMode="tel" autoComplete="tel"
                     value={telefone} onChange={(e) => setTelefone(mascaraTel(e.target.value))} />
              <span className="ajuda">É por aqui que a clínica confirma.</span>
            </div>

            {v.exige_cpf && (
              <div className="campo">
                <label htmlFor="ag-cpf">CPF</label>
                <input id="ag-cpf" className="entrada mono" required inputMode="numeric"
                       value={cpf} onChange={(e) => setCpf(digitos(e.target.value).slice(0, 11))} />
              </div>
            )}

            {v.exige_nascimento && (
              <div className="campo">
                <label htmlFor="ag-nasc">Data de nascimento</label>
                <input id="ag-nasc" className="entrada" type="date" required
                       value={nascimento} onChange={(e) => setNascimento(e.target.value)} />
              </div>
            )}

            <button className="botao" type="submit"
                    disabled={enviando || nome.trim().length < 3 || digitos(telefone).length < 10}>
              {enviando ? 'Marcando…' : 'Confirmar horário'}
            </button>
            <button type="button" className="ag__voltar"
                    onClick={() => setPasso(3)}>← Trocar o horário</button>
          </form>
        )}
      </div>
    </main>
  )
}
